import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CalendarCheck, Sun, CloudRain, CloudLightning, Snowflake, Wind, ThermometerSun, ThermometerSnowflake, Zap, ArrowUp, ArrowDown } from "lucide-react";
import { RollingNumber } from "@/components/broadcast/shared";

export function useOutlookScore() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/outlook-score").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30 * 60 * 1000);
    return () => clearInterval(t);
  }, []);
  return data;
}

export function hasOutlookScore(data) {
  return !!(data && data.available && (data.days || []).length >= 3);
}

const ICONS = {
  severe: Zap,
  storm: CloudLightning,
  rain: CloudRain,
  snow: Snowflake,
  wind: Wind,
  hot: ThermometerSun,
  cold: ThermometerSnowflake,
  sun: Sun,
};

export function OutlookScoreSlide({ data }) {
  const days = (data?.days || []).slice(0, 7);
  if (!days.length) return null;
  return (
    <div data-testid="outlook-score-slide" className="relative h-full w-full overflow-hidden" style={{ background: "linear-gradient(140deg, #14233f 0%, #0c1830 58%, #0a0f1c 100%)" }}>
      <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #fff 0 2px, transparent 2px 28px)" }} />
      <div className="relative flex h-full w-full flex-col px-[54px] py-[30px]">
        <div className="flex items-center gap-2.5">
          <span className="flex items-center gap-2 rounded-full bg-[#2f7bed] px-4 py-1.5 font-oswald text-[20px] font-black uppercase tracking-[0.2em] text-white">
            <CalendarCheck className="h-5 w-5" strokeWidth={2.6} /> Daily Outlook Score
          </span>
        </div>
        <div className="mt-2 flex items-end justify-between">
          <div className="font-oswald text-[50px] font-black uppercase leading-none text-white">The Week Ahead</div>
          <div className="font-plex text-[20px] font-semibold text-white/75">10 = Great day · 1 = High impact</div>
        </div>

        <div className="mt-5 grid flex-1 gap-[12px]" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
          {days.map((d, i) => {
            const Icon = ICONS[d.icon] || Sun;
            return (
              <div key={d.date} className="flex flex-col overflow-hidden rounded-[12px] bg-white/[0.05] ring-1 ring-white/12" data-testid={`outlook-score-day-${d.date}`}>
                <div className="h-[8px] w-full" style={{ background: d.color }} />
                <div className="flex flex-col items-center gap-0.5 px-2 pt-3">
                  <span className="font-oswald text-[26px] font-black uppercase leading-none text-white" data-testid={`outlook-score-label-${d.date}`}>{d.label}</span>
                  <span className="font-plex text-[18px] font-semibold text-white/50">{d.md}</span>
                </div>
                <div className="mt-2 flex items-baseline justify-center" style={{ color: d.color }}>
                  <RollingNumber value={d.score} delay={i * 90} className="font-oswald text-[72px] font-black leading-none tabular-nums" testId={`outlook-score-value-${d.date}`} />
                  <span className="font-oswald text-[24px] font-black leading-none opacity-70">/10</span>
                </div>
                <div className="mt-1 text-center font-oswald text-[20px] font-black uppercase leading-none" style={{ color: d.color }} data-testid={`outlook-score-level-${d.date}`}>{d.level}</div>
                <div className="mt-1 flex min-h-[24px] items-center justify-center gap-1" data-testid={`outlook-score-trend-${d.date}`}>
                  {d.trend === "up" ? (
                    <span className="flex items-center gap-1 font-oswald text-[19px] font-black uppercase tracking-wide text-[#3fbf54]"><ArrowUp className="h-[19px] w-[19px]" strokeWidth={3} /> Better</span>
                  ) : d.trend === "down" ? (
                    <span className="flex items-center gap-1 font-oswald text-[19px] font-black uppercase tracking-wide text-[#f6a21c]"><ArrowDown className="h-[19px] w-[19px]" strokeWidth={3} /> Worse</span>
                  ) : (
                    <span className="font-oswald text-[19px] font-black uppercase tracking-wide text-white/25">—</span>
                  )}
                </div>
                <div className="mt-auto flex flex-col items-center gap-2 px-2.5 pb-3 pt-3">
                  <Icon className="h-[40px] w-[40px] text-[#9fb6dc]" strokeWidth={2} />
                  <span className="min-h-[46px] text-center font-plex text-[21px] font-semibold leading-tight text-white/85" data-testid={`outlook-score-reason-${d.date}`}>{d.reason}</span>
                  <span className="font-oswald text-[22px] font-bold tabular-nums text-white/70">
                    {d.high != null ? `${d.high}°` : "—"}<span className="mx-1 text-white/30">/</span><span className="text-[#9db4d4]">{d.low != null ? `${d.low}°` : "—"}</span>
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-3 font-plex text-[19px] font-medium tracking-wide text-white/60">
          GAWX planner score · statewide worst-case from NWS forecasts + SPC severe outlook · higher means a calmer, easier day
        </div>
      </div>
    </div>
  );
}
